'use client';

import { c as _c } from "react/compiler-runtime";
import { AnimatePresence, motion } from 'motion/react';
import { usePathname, useRouter } from 'next/navigation';
import React, { cloneElement, createContext, useContext, useEffect, useState } from 'react';
export const TransitionInterceptContext = createContext<{
  close: () => void;
} | null>(null);
interface TransitionInterceptProviderProps {
  children: React.ReactNode;
  paths?: string[];
  excludePaths?: string[];
}
function normalizePath(path: string) {
  if (!path) return '/';
  return path.endsWith('/') && path !== '/' ? path.slice(0, -1) : path;
}
export function TransitionInterceptProvider({
  children,
  paths = [],
  excludePaths = []
}: TransitionInterceptProviderProps) {
  const router = useRouter();
  const pathname = usePathname();
  const normalizedPaths = paths.map(normalizePath);
  const normalizedExcludePaths = excludePaths.map(normalizePath);
  const isExcluded = normalizedExcludePaths.some(path => pathname === path || pathname.startsWith(`${path}/`));
  const isModalActive = !isExcluded && normalizedPaths.some(path_0 => pathname === path_0 || pathname.startsWith(`${path_0}/`));
  const [shouldClose, setShouldClose] = useState(false);
  const isOpen = isModalActive && !shouldClose;
  useEffect(() => {
    setShouldClose(false);
  }, [pathname]);
  useEffect(() => {
    if (!isOpen) return;
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
    document.body.style.overflow = 'hidden';
    document.body.style.paddingRight = `${scrollbarWidth}px`;
    const handleEsc = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        close();
      }
    };
    document.addEventListener('keydown', handleEsc);
    return () => {
      document.body.style.overflow = '';
      document.body.style.paddingRight = '';
      document.removeEventListener('keydown', handleEsc);
    };
  }, [isOpen]);
  const close = () => {
    setShouldClose(true);
  };
  return <TransitionInterceptContext.Provider value={{
    close
  }}>
      {!isModalActive ? children : <AnimatePresence onExitComplete={() => {
      if (shouldClose) {
        router.back();
      }
    }}>
          {isOpen && <motion.div initial={{
        y: '100%'
      }} animate={{
        y: 0
      }} exit={{
        y: '100%'
      }} transition={{
        duration: 0.3,
        ease: 'easeInOut'
      }} className='fixed inset-0 z-200 flex flex-col overflow-y-auto overscroll-contain bg-black' data-lenis-prevent='' data-lenis-prevent-wheel='' data-lenis-prevent-touch=''>
              {children}
            </motion.div>}
        </AnimatePresence>}
    </TransitionInterceptContext.Provider>;
}
interface TransitionInterceptBackTriggerProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  children: React.ReactNode;
  asChild?: boolean;
}
export function TransitionInterceptBackTrigger(t0) {
  const $ = _c(15);
  let children;
  let props;
  let t1;
  if ($[0] !== t0) {
    ({
      children,
      asChild: t1,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = props;
    $[3] = t1;
  } else {
    children = $[1];
    props = $[2];
    t1 = $[3];
  }
  const asChild = t1 === undefined ? false : t1;
  const context = useContext(TransitionInterceptContext);
  let t2;
  if ($[4] !== context) {
    t2 = context || {};
    $[4] = context;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const {
    close
  } = t2;
  let t3;
  if ($[6] !== close) {
    t3 = () => {
      if (close) {
        close();
      }
    };
    $[6] = close;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const handleClick = t3;
  if (asChild) {
    let t4;
    if ($[8] !== children || $[9] !== handleClick) {
      const child = React.Children.only(children) as React.ReactElement<{
        onClick?: (e: React.MouseEvent) => void;
      }>;
      t4 = cloneElement(child, {
        ...child.props,
        onClick: e => {
          child.props.onClick?.(e);
          handleClick();
        }
      });
      $[8] = children;
      $[9] = handleClick;
      $[10] = t4;
    } else {
      t4 = $[10];
    }
    return t4;
  }
  let t4;
  if ($[11] !== children || $[12] !== handleClick || $[13] !== props) {
    t4 = <button type="button" onClick={handleClick} {...props}>{children}</button>;
    $[11] = children;
    $[12] = handleClick;
    $[13] = props;
    $[14] = t4;
  } else {
    t4 = $[14];
  }
  return t4;
}